import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { CurrencyText, DimmerLoader, FormField, InfoList, Modal, TextField, TextInput } from 'views/components'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { walletApiEvents } from 'modules/api/wallet'
import { agencyApiEvents, agencyApiSelectors } from 'modules/api/agency'
import { apiStateSelectors } from 'modules/api/apiState'
import { reportingApiEvents, reportingApiSelectors } from 'modules/api/reporting'
import { getAgencyHeading } from 'utils/agency'
import { formatStandard } from 'utils/date'
import { SuspenseReference } from '../../../types'
import { getAllocationErrorMessage, WALLET_NUMBER_PATTERN } from '../../../utils'
import styles from './AllocationModal.module.scss'

interface AllocationFormValues {
  walletNumber: string
  clickupRef: string
  notes: string
}

interface AllocationModalProps {
  isOpen: boolean
  onClose: () => void
  currentReference?: SuspenseReference | null
  onAllocated?: () => void
}

const initialValues: AllocationFormValues = {
  walletNumber: '',
  clickupRef: '',
  notes: '',
}

const validationSchema = yup.object().shape({
  walletNumber: yup
    .string()
    .trim()
    .required('required')
    .matches(WALLET_NUMBER_PATTERN, 'Wallet numbers are 10 digits, e.g. 3643808961'),
  clickupRef: yup.string().trim().required('required'),
})

interface ConfirmStepProps {
  values: AllocationFormValues
  currentReference: SuspenseReference
  /** Why the API rejected the last attempt */
  serverError?: string | null
}

const ConfirmStep = ({ values, currentReference, serverError }: ConfirmStepProps): React.ReactElement => {
  const walletNumber = values.walletNumber.trim()
  const clickupRef = values.clickupRef.trim()

  const lookup = useSelector(state => reportingApiSelectors.getSuspenseReferenceLookup(state)(walletNumber))
  const agency = useSelector(state => agencyApiSelectors.getAgencyFromListingById(state)(lookup?.agencyId))
  const isLoading = useSelector(state =>
    apiStateSelectors.isLoading(state)([reportingApiEvents.suspenseReferenceLookup_request]),
  )

  return (
    <DimmerLoader loading={isLoading}>
      <p className={styles.summary}>
        You are about to allocate{' '}
        <strong>
          <CurrencyText>{currentReference.amount ?? 0}</CurrencyText>
        </strong>{' '}
        to wallet <strong>{walletNumber}</strong>.
      </p>
      {serverError && <p className={styles.error}>{serverError} Go back to change it.</p>}
      {!isLoading && !lookup ? (
        <p className={styles.error}>No wallet was found with this number. Go back and check it.</p>
      ) : (
        <InfoList
          className={styles.info}
          spacing="sm"
          items={[
            { label: 'Contact', value: lookup?.contactName },
            { label: 'Account type', value: lookup?.accountType },
            { label: 'Agency', value: agency ? getAgencyHeading(agency) : undefined },
            { label: 'Property', value: lookup?.propertyAddress },
            {
              label: 'ClickUp ticket',
              value: (
                <a href={clickupRef} target="_blank" rel="noopener noreferrer">
                  {clickupRef}
                </a>
              ),
            },
            { label: 'Notes', value: values.notes, isVisible: !!values.notes.trim() },
          ]}
        />
      )}
    </DimmerLoader>
  )
}

const AllocationModal = ({
  isOpen,
  onClose,
  currentReference,
  onAllocated,
}: AllocationModalProps): null | React.ReactElement => {
  const dispatch = useDispatch()
  const [step, setStep] = useState<'details' | 'confirm'>('details')
  const formikRef = useRef<FormikProps<AllocationFormValues>>(null)

  const getLookup = useSelector(reportingApiSelectors.getSuspenseReferenceLookup)

  // Notes start from the receipt's saved note, as the allocation's notes replace it.
  const savedNote = currentReference?.notes ?? ''
  const formInitialValues = useMemo(() => ({ ...initialValues, notes: savedNote }), [savedNote])

  /**
   * The modal stays open until the API answers, so a rejected wallet number can be fixed here.
   * apiState never clears an event's errors, so a failure is an error entry that differs from the
   * one held when the request went out, not merely one being present.
   */
  const [pending, setPending] = useState<{
    action: ReturnType<typeof walletApiEvents.allocateSuspenseAccount_request>
    errorsBefore: unknown
    clearNote: boolean
  } | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const getErrors = useSelector(apiStateSelectors.getErrorsByEvent)
  const isAllocating = useSelector(state =>
    apiStateSelectors.isLoading(state)([walletApiEvents.allocateSuspenseAccount_request]),
  )
  const latestErrors = pending ? getErrors(pending.action) : undefined

  const handleClose = (): void => {
    setStep('details')
    setServerError(null)
    setPending(null)
    formikRef.current?.resetForm()
    onClose()
  }

  useEffect(() => {
    if (!pending || isAllocating) return
    setPending(null)
    if (latestErrors !== pending.errorsBefore) {
      const errorList = Array.isArray(latestErrors) ? latestErrors : []
      setServerError(getAllocationErrorMessage(errorList[0]?.message))
      return
    }
    // An allocation without notes keeps the saved one, so emptying the field clears it here.
    if (pending.clearNote) {
      dispatch(
        walletApiEvents.saveSuspenseNote_request({ params: pending.action.payload.params, body: { notes: null } }),
      )
    }
    handleClose()
    onAllocated?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, isAllocating, latestErrors])

  if (!currentReference) return null

  const title = `Allocate ${currentReference.referenceUsed ?? ''}`.trim()

  return (
    <Formik
      key={currentReference.txId}
      innerRef={formikRef}
      validationSchema={validationSchema}
      initialValues={formInitialValues}
      // Picks up the saved note if it changes after the modal mounts.
      enableReinitialize={true}
      onSubmit={values => {
        dispatch(reportingApiEvents.suspenseReferenceLookup_request({ reference: values.walletNumber.trim() }))
        dispatch(agencyApiEvents.agencyListing_request())
        setStep('confirm')
      }}
    >
      {({ values, errors, touched, handleBlur, handleChange, submitForm }: FormikProps<AllocationFormValues>) => {
        const walletNumber = values.walletNumber.trim()
        const canAllocate = !!getLookup(walletNumber)

        const handleAllocate = (): void => {
          // `txId` is a path segment and `correctReference` is required by the API, so without
          // either the request could only fail.
          if (!currentReference.txId || !walletNumber || !canAllocate || isAllocating) return
          const action = walletApiEvents.allocateSuspenseAccount_request({
            params: { txId: currentReference.txId },
            body: {
              correctReference: walletNumber,
              notes: values.notes.trim() || undefined,
              clickupReference: values.clickupRef.trim(),
            },
          })
          setServerError(null)
          setPending({ action, errorsBefore: getErrors(action), clearNote: !!savedNote && !values.notes.trim() })
          dispatch(action)
        }

        const actions =
          step === 'details'
            ? [
                {
                  label: 'Continue',
                  onClick: submitForm,
                  disabled: !walletNumber || !values.clickupRef.trim(),
                },
                { label: 'Cancel', onClick: handleClose },
              ]
            : [
                {
                  label: isAllocating ? 'Allocating…' : 'Yes, allocate',
                  onClick: handleAllocate,
                  disabled: !canAllocate || isAllocating,
                },
                {
                  label: 'Back',
                  onClick: () => {
                    setServerError(null)
                    setStep('details')
                  },
                  disabled: isAllocating,
                },
                { label: 'Cancel', onClick: handleClose },
              ]

        return (
          <Modal
            className={styles.root}
            isOpen={isOpen}
            padding="sm"
            width="lg"
            showClose={false}
            onClose={handleClose}
          >
            <Modal.Header
              title={step === 'details' ? title : 'Are you sure?'}
              subtitle={
                <>
                  <CurrencyText>{currentReference.amount ?? 0}</CurrencyText> received{' '}
                  {formatStandard(currentReference.dateReceived)}
                </>
              }
            />
            <Modal.Body actions={actions}>
              {step === 'details' ? (
                <form
                  onSubmit={e => {
                    e.preventDefault()
                    void submitForm()
                  }}
                >
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.text}
                          name="walletNumber"
                          placeholder="e.g. 3643808961"
                          value={values.walletNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      label="Wallet number"
                      required
                      error={touched.walletNumber && errors.walletNumber}
                    />
                  </FormField>
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.url}
                          name="clickupRef"
                          placeholder="Paste the ClickUp ticket link"
                          value={values.clickupRef}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      label="ClickUp ticket"
                      required
                      error={touched.clickupRef && errors.clickupRef}
                    />
                  </FormField>
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.text}
                          name="notes"
                          placeholder="Why this wallet?"
                          value={values.notes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      label="Notes"
                      textarea={true}
                    />
                  </FormField>
                </form>
              ) : (
                <ConfirmStep values={values} currentReference={currentReference} serverError={serverError} />
              )}
            </Modal.Body>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default AllocationModal
